import React, { useEffect, useState, FC } from 'react';
import { Form, Button, Alert } from 'Components/nui';
import { FormWrapper } from 'Components/form';
import { mergeSchema, normalizeChoices } from 'Components/form/utils';
import { ShowResult } from 'Components/Result';
import Icon from 'Components/Icon';
import { useStoreState } from 'easy-peasy';
import { promisify } from 'es6-promisify';
import { useAsync } from 'react-use';
import { useQuery } from '@tanstack/react-query';
import { api } from '~/api';
import { submitError, submitSuccess } from 'Components/form/utils';
import { FormattedMessage } from 'react-intl';
import { useProductById } from '~/hooks/useProducts';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import { PriceList } from './PriceList';
import { fetchOrderPrices } from './fetchPrices';

interface Price {
  val: number;
  unit: string;
  reason?: string;
}

interface CustomerPrice {
  id: string;
  name: string;
  price: Price;
}

interface PortPrice {
  id: string;
  country: { name: string };
  port: { name: string };
  zone?: { name: string };
  price: Price;
  count: number;
}

interface Product {
  id: string;
  step: number;
  loading?: Array<{ id: string; desc: string }>;
}

interface Schema {
  [key: string]: any;
}

function getFormWrapper({
  form,
  product,
  schema,
  initialData,
}: {
  form: WrappedFormUtils;
  product: Product;
  schema: Schema;
  initialData: any;
}) {
  const getChoicesByDivision =
    ({ choices }: { choices: any }) =>
    () => {
      const divisionId = form.getFieldValue('division');
      return normalizeChoices(
        Array.isArray(choices) ? choices : (choices?.[divisionId] ?? [])
      );
    };

  const fields = [
    {
      name: 'division',
      choices: normalizeChoices,
    },
    {
      name: 'loading',
      choices: (product.loading || [])
        .map((x) => ({
          key: x.id,
          value: x.id,
          label: x.desc,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
    {
      name: 'price',
      step: product.step,
      min: product.step,
    },
    {
      name: 'fromaddr',
      choices: getChoicesByDivision,
    },
  ];

  return new FormWrapper(form, initialData, mergeSchema(schema, fields));
}

interface NFormProps {
  form: WrappedFormUtils;
  product: Product;
  schema: Schema;
  submitState: any;
  initialData: any;
  auto?: boolean;
}

function NForm({
  form,
  product,
  schema,
  submitState,
  initialData,
  auto,
}: NFormProps) {
  const solution = useStoreState((state) => state.auth.solution);
  const zones = solution?.freight_types?.includes('zone') ?? false;

  const [prices, setPrices] = useState<
    CustomerPrice[] | PortPrice[] | undefined
  >();
  const [format, setFormat] = useState<'port' | 'customer' | undefined>();
  const validateFieldsAndScroll = promisify(form.validateFieldsAndScroll);

  useEffect(() => {
    if (auto) handleSubmit();
  }, [auto]);

  if (!product) return null;

  const formWrapper = getFormWrapper({
    form,
    product,
    schema,
    initialData,
  });

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    try {
      await validateFieldsAndScroll();
    } catch {
      return;
    }
    submitState.setLoading(true);
    const params = formWrapper.serialize();
    try {
      setPrices(undefined);
      const result = await api.getData2(
        `/products/${product.id}/displayed-prices`,
        params
      );
      if (result.format === 'port') {
        setFormat('port');
        const sortedPrices = [...result.prices].sort((a, b) => {
          const countryCompare = (a.country?.name ?? '').localeCompare(
            b.country?.name ?? ''
          );
          if (countryCompare !== 0) return countryCompare;
          return (a.port?.name ?? '').localeCompare(b.port?.name ?? '');
        });
        setPrices(sortedPrices);
      } else if (result.format === 'customer') {
        setFormat('customer');
        setPrices(
          [...result.prices].sort((a, b) => a.name.localeCompare(b.name))
        );
      }
    } catch (err: any) {
      const errors = err?.response?.data?.errors?.[0]?.description;
      if (errors) {
        formWrapper.setErrors(errors);
      } else {
        submitState.setResult(err);
      }
    }
    submitState.setLoading(false);
  }

  return (
    <div>
      <div className="customer-prices-form">
        <div className="nui-row tw:px-0 tw:w-full">
          <Form>
            {formWrapper.render()}
            <Form.Item className="tw:!mt-2">
              <Button
                htmlType="button"
                onClick={handleSubmit}
                loading={submitState.loading}
              >
                <FormattedMessage id="show" defaultMessage="Show" />
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>

      {prices && format && (
        <div className="tw:mt-3">
          <PriceList prices={prices} format={format} zones={zones} />
        </div>
      )}
    </div>
  );
}

const WrappedForm = Form.create()(NForm);

interface DisplayedPricesProps {
  productId?: string;
  orderId?: string;
  init?: any;
  auto?: boolean;
}

// Component for order mode - shows published/historical prices
const OrderPricesView: FC<{ orderId: string }> = ({ orderId }) => {
  const solution = useStoreState((state) => state.auth.solution);
  const zones = solution?.freight_types?.includes('zone') ?? false;

  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['orders', orderId, 'displayed-prices'],
    queryFn: () => fetchOrderPrices(orderId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  if (loading) {
    return (
      <div className="tw tw:flex tw:justify-center tw:py-8">
        <Icon
          name="progress_activity"
          className="tw:animate-spin tw:text-2xl tw:text-gray-400"
        />
      </div>
    );
  }

  if (error) {
    return (
      <Alert type="error" hasicon>
        <FormattedMessage
          id="error-loading-prices"
          defaultMessage="Error loading prices"
        />
      </Alert>
    );
  }

  if (data) {
    return (
      <PriceList prices={data.prices} format={data.format} zones={zones} />
    );
  }

  return null;
};

const DisplayedPrices: FC<DisplayedPricesProps> = ({
  productId,
  orderId,
  init,
  auto,
}) => {
  // Call hooks at top level (before conditionals)
  const product = useProductById(productId || '');

  const schemaState = useAsync(() => {
    if (!productId) return Promise.resolve();
    return api.getData({
      type: 'products',
      id: `${productId}/displayed-prices/schema`,
    });
  }, [productId]);

  // Order mode: use dedicated component
  if (orderId) {
    return <OrderPricesView orderId={orderId} />;
  }

  // Product mode: existing behavior with form
  if (schemaState.loading) {
    return (
      <div className="tw tw:flex tw:justify-center tw:py-8">
        <Icon
          name="progress_activity"
          className="tw:animate-spin tw:text-2xl tw:text-gray-400"
        />
      </div>
    );
  }

  if (schemaState.error) {
    return (
      <Alert type="error" hasicon>
        <FormattedMessage
          id="error-loading-displayed-prices-schema"
          defaultMessage="Error loading price filters"
        />
      </Alert>
    );
  }

  return (
    <ShowResult renderSuccess={submitSuccess} renderError={submitError}>
      {(submitState) => (
        <div className="nui-form">
          <WrappedForm
            key="customer-prices"
            schema={schemaState.value}
            product={product}
            initialData={init}
            submitState={submitState}
            auto={auto}
          />
        </div>
      )}
    </ShowResult>
  );
};

export default DisplayedPrices;
